/**
 * Store de toasts (notifications éphémères dans l'app).
 *
 * Remplace les `alert()` natifs et les retours d'erreur silencieux. Tout module
 * peut appeler `useToasts().push(...)`. Les toasts s'auto-dismissent après un
 * délai configurable, ou restent affichés si `duration === 0`.
 */
import { defineStore } from 'pinia'

export type ToastKind = 'success' | 'error' | 'info' | 'warning'

export interface Toast {
  id: number
  kind: ToastKind
  title: string
  message?: string
  duration: number // ms, 0 = sticky
  createdAt: number
}

const DEFAULT_DURATIONS: Record<ToastKind, number> = {
  success: 3500,
  info: 4000,
  warning: 5000,
  error: 6000,
}

let _nextId = 1

export const useToasts = defineStore('toasts', {
  state: () => ({
    toasts: [] as Toast[],
  }),
  actions: {
    push(opts: {
      kind?: ToastKind
      title: string
      message?: string
      duration?: number
    }): number {
      const kind = opts.kind || 'info'
      const id = _nextId++
      const duration = opts.duration ?? DEFAULT_DURATIONS[kind]
      this.toasts.push({
        id,
        kind,
        title: opts.title,
        message: opts.message,
        duration,
        createdAt: Date.now(),
      })
      if (duration > 0) {
        setTimeout(() => this.dismiss(id), duration)
      }
      return id
    },

    success(title: string, message?: string) {
      return this.push({ kind: 'success', title, message })
    },
    error(title: string, message?: string) {
      return this.push({ kind: 'error', title, message })
    },
    info(title: string, message?: string) {
      return this.push({ kind: 'info', title, message })
    },
    warning(title: string, message?: string) {
      return this.push({ kind: 'warning', title, message })
    },

    dismiss(id: number) {
      this.toasts = this.toasts.filter((t) => t.id !== id)
    },

    clear() {
      this.toasts = []
    },
  },
})
